/**
 * Jobs Page
 *
 * Main hub for job management:
 * - View all jobs (active, draft, delivered)
 * - Create new jobs (chase list upload flow)
 * - Click into a job to see details and manage station-track links
 */

import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Spinner,
  Table,
  InputGroup,
  InputGroupText,
  Input,
} from 'reactstrap';
import moment from 'moment';
import PageContainer from '../../components/global/PageContainer';
import { withAuthorization, withOrganization } from '../../components/Session';
import { FirebaseContext } from '../../components/Firebase';
import { useOrganizationStore } from '../../store/organization.store';
import { useTracksStore } from '../../store/tracks.store';
import { getCampaignName } from '../../utils/campaign';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { getOrgCollectionPath } from '../../constants/paths';
import ChaseListUploader from '../../components/MediaPlan/ChaseListUploader';
import GenericConfirmModal from '../../components/GenericModal/ConfirmModal';
import * as ROUTES from '../../constants/routes';

interface Job {
  id: string;
  client: string | { label: string; value: string };
  campaign: string;
  period: string;
  /** Two-status model: draft (pre-delivery, any point) or delivered.
   *  Legacy 'active' docs are treated as draft on read (see the badge
   *  switch in getStatusBadge). Rejected is still valid on the
   *  per-delivery level but not on the job doc itself. */
  status: 'draft' | 'delivered' | 'active';
  stationCount: number;
  uploadedAt: number;
  uploadedByName: string;
  fileName: string;
  stations?: any[];
}

type SortKey = 'status' | 'client' | 'campaign' | 'period' | 'stationCount' | 'linked' | 'uploadedAt' | 'source';
type SortDir = 'asc' | 'desc';

const Jobs: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploader, setShowUploader] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('uploadedAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [deleteTarget, setDeleteTarget] = useState<Job | null>(null);

  const firebase = useContext(FirebaseContext);
  const history = useHistory();
  const { currentOrgId } = useOrganizationStore();
  // Live tracks — used by getLinkedCount below to only count stations
  // whose linkedTrackId still resolves to a real audiometa doc. Same
  // dangling-link semantics as JobDetail's hasValidLink helper: a
  // deleted-from-library track leaves the station's linkedTrackId
  // pointing at nothing, and we want the campaign row's "X/Y linked"
  // badge to reflect that immediately.
  const tracks = useTracksStore(state => state.tracks);

  // Live subscription to jobs — so download-badge fields (deliveryCount
  // / downloadedDeliveryCount, populated by the stampDeliveryDownload
  // CF) reflect updates without needing a page refresh. Same pattern
  // as Dashboard.tsx.
  useEffect(() => {
    if (!currentOrgId || !firebase) {
      setJobs([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const jobsRef = collection(
      firebase.db,
      getOrgCollectionPath(currentOrgId, 'jobs')
    );
    const jobsQuery = query(jobsRef, orderBy('uploadedAt', 'desc'));
    const unsub = onSnapshot(jobsQuery, (snapshot) => {
      const loadedJobs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Job[];
      setJobs(loadedJobs);
      setLoading(false);
    }, (err) => {
      console.error('Error loading jobs:', err);
      setLoading(false);
    });
    return () => unsub();
  }, [currentOrgId, firebase]);

  // Opens the styled confirm modal; actual deletion happens in confirmDeleteJob.
  const handleDeleteJob = (e: React.MouseEvent, job: Job) => {
    e.stopPropagation(); // Prevent row click navigation
    setDeleteTarget(job);
  };

  const confirmDeleteJob = async () => {
    if (!deleteTarget || !currentOrgId || !firebase) return;
    const jobId = deleteTarget.id;
    const isCopy = !!(deleteTarget as any).isCampaignCopy || !!(deleteTarget as any).isDeliveredJob;
    setDeletingId(jobId);
    try {
      if (isCopy) {
        // Deleting a local COPY (agency/studio/broadcaster viewing
        // someone else's campaign). Remove only the local doc — don't
        // touch the source or other orgs. If the org wants the whole
        // campaign gone system-wide, the source org has to do it.
        await firebase.deleteJobDeep(currentOrgId, jobId);
      } else {
        // Deleting the SOURCE campaign — cascade to studio + agency +
        // broadcaster copies + all associated audiometa + public
        // deliveryCode lookups + pending notifications. Model A full
        // sync: source-driven deletion pulls the campaign from every
        // downstream org.
        await firebase.cascadeDeleteCampaign(currentOrgId, jobId);
      }
      setJobs(prev => prev.filter(j => j.id !== jobId));
    } catch (err: any) {
      console.error('Error deleting job:', err);
    }
    setDeletingId(null);
  };

  const getClientName = (client: string | { label: string; value: string } | undefined): string => {
    if (!client) return '--';
    if (typeof client === 'string') return client;
    return client.label || '--';
  };

  const getStatusBadge = (status: string, job?: Job) => {
    if ((job as any)?.isDeliveredJob) {
      switch (status) {
        case 'accepted': return <Badge style={{ backgroundColor: 'rgba(80, 200, 120, 0.3)', color: '#50c878' }}>Accepted</Badge>;
        case 'delivered':
        default: return <Badge style={{ backgroundColor: 'rgba(80, 200, 120, 0.3)', color: '#50c878' }}>Received</Badge>;
      }
    }
    switch (status) {
      case 'delivered':
        return <Badge style={{ backgroundColor: 'var(--accent-1-color)', color: '#fff' }}>Delivered</Badge>;
      case 'draft':
      default:
        // Legacy 'active' docs fall through here and render as Draft.
        // Passive migration — no data fix needed; next save will
        // overwrite the stored string.
        return <Badge style={{ backgroundColor: 'rgba(255, 180, 50, 0.3)', color: '#ffb432' }}>Draft</Badge>;
    }
  };

  const getLinkedCount = (job: Job): number => {
    if (!job.stations) return 0;
    // Only counts stations whose linkedTrackId still resolves to a
    // real track in the store. Dangling links (track deleted from
    // library) count as unlinked so the "X/Y linked" badge reflects
    // truth in real time.
    return job.stations.filter((s: any) => s?.linkedTrackId && tracks[s.linkedTrackId]).length;
  };

  const getLiveReadCount = (job: Job): number => {
    if (!job.stations) return 0;
    return job.stations.filter((s: any) => s.isLiveRead).length;
  };

  /** Denominator for the Linked column — total stations MINUS live reads,
   *  since live reads never get an audio link. Green threshold uses this
   *  so an all-live-read campaign, or a mixed one where every non-LR spot
   *  is linked, still hits the "ready" colour. */
  const getLinkableStationCount = (job: Job): number => {
    return (job.stationCount || 0) - getLiveReadCount(job);
  };

  const getSourceLabel = (job: Job): string => {
    // Any cross-org copy (delivered to a broadcaster OR synced to a
    // studio/agency related-role org) shows the SOURCE ORG'S name. The
    // `fileName` fallback is meaningful only for the source-authored
    // job in its own org — it's the chase-list file that spawned the
    // job, which means nothing to a recipient.
    const isCrossOrgCopy = (job as any).isDeliveredJob || (job as any).isCampaignCopy;
    if (isCrossOrgCopy) return (job as any).sourceOrgName || '';
    return job.fileName || '';
  };

  // Pull the sortable scalar for a given column
  const sortValue = (job: Job, key: SortKey): string | number => {
    switch (key) {
      case 'status': return job.status || '';
      case 'client': return getClientName(job.client).toLowerCase();
      case 'campaign': return getCampaignName(job.campaign, '').toLowerCase();
      case 'period': return (job.period || '').toLowerCase();
      case 'stationCount': return job.stationCount || 0;
      case 'linked': return getLinkedCount(job);
      case 'uploadedAt': return job.uploadedAt || 0;
      case 'source': return getSourceLabel(job).toLowerCase();
      default: return '';
    }
  };

  const displayedJobs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = !q ? jobs : jobs.filter(j => {
      const haystack = [
        getClientName(j.client),
        getCampaignName(j.campaign, ''),
        j.period,
        j.status,
        j.fileName,
        j.uploadedByName,
        (j as any).sourceOrgName,
        (j as any).isDeliveredJob ? 'received' : '',
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });

    const sorted = [...filtered].sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [jobs, searchQuery, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'uploadedAt' ? 'desc' : 'asc');
    }
  };

  const sortIndicator = (key: SortKey) => {
    if (sortKey !== key) return <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>;
    return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const headerStyle: React.CSSProperties = {
    border: 'none', padding: '0.5rem', cursor: 'pointer', userSelect: 'none',
    fontWeight: 600,
  };

  return (
    // Bypass the shared PageContainer's fixed-width Container so the
    // Jobs table can use the full viewport width — the Delivered +
    // Downloaded badges pushed the Status column wide, and the extra
    // room lets everything render on one line. Matches the "wrapper
    // pt-3 pb-3" outer padding used elsewhere but drops the bootstrap
    // max-width constraint.
    <div className="wrapper pt-3 pb-3" style={{ paddingLeft: '1.5rem', paddingRight: '1.5rem' }}>
      <Container fluid style={{ maxWidth: 'none' }}>
        <Row>
          <Col>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 className="text-uppercase mb-0">
                <i className="tim-icons icon-bullet-list-67 accent-1-icon icon-medium mr-2"></i>{' '}
                Campaigns
              </h2>
              <Button
                className="btn-primary-custom"
                onClick={() => setShowUploader(true)}
              >
                <span style={{ position: 'relative', zIndex: 2 }}>
                  <i className="tim-icons icon-simple-add mr-2"></i>
                  New Campaign
                </span>
              </Button>
            </div>

            {jobs.length > 0 && (
              <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 12 }}>
                <InputGroup className="d-flex" style={{ flex: 1, maxWidth: 420, marginBottom: 0 }}>
                  <InputGroupText style={{
                    color: 'rgba(255,255,255,0.4)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    minWidth: 38,
                  }}>
                    <i className="tim-icons icon-zoom-split" style={{ lineHeight: 1, display: 'inline-block' }} />
                  </InputGroupText>
                  <Input
                    type="text"
                    placeholder="Search campaigns"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ color: '#fff' }}
                  />
                </InputGroup>
                {searchQuery && (
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                    {displayedJobs.length} / {jobs.length}
                  </span>
                )}
              </div>
            )}

            <Card className="no-bg-card">
              <CardBody>
                {loading ? (
                  <div className="text-center py-4">
                    <Spinner style={{ color: 'var(--accent-1-color)' }} />
                  </div>
                ) : jobs.length === 0 ? (
                  <div className="text-center py-4">
                    <p style={{ color: 'rgba(255, 255, 255, 0.5)', marginBottom: '1rem' }}>
                      No campaigns yet. Upload a chase list to create your first campaign.
                    </p>
                    <Button
                      className="btn-primary-custom"
                      onClick={() => setShowUploader(true)}
                    >
                      <span style={{ position: 'relative', zIndex: 2 }}>Upload Chase List</span>
                    </Button>
                  </div>
                ) : (
                  <Table dark responsive size="sm" style={{ backgroundColor: 'transparent' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        <th style={{ ...headerStyle, whiteSpace: 'nowrap', minWidth: '230px' }} onClick={() => toggleSort('status')}>Status{sortIndicator('status')}</th>
                        <th style={headerStyle} onClick={() => toggleSort('client')}>Client{sortIndicator('client')}</th>
                        <th style={headerStyle} onClick={() => toggleSort('campaign')}>Campaign{sortIndicator('campaign')}</th>
                        <th style={headerStyle} onClick={() => toggleSort('period')}>Period{sortIndicator('period')}</th>
                        <th style={headerStyle} onClick={() => toggleSort('stationCount')}>Stations{sortIndicator('stationCount')}</th>
                        <th style={headerStyle} onClick={() => toggleSort('linked')}>Linked{sortIndicator('linked')}</th>
                        <th style={headerStyle} onClick={() => toggleSort('uploadedAt')}>Created{sortIndicator('uploadedAt')}</th>
                        <th style={headerStyle} onClick={() => toggleSort('source')}>Source{sortIndicator('source')}</th>
                        <th style={{ border: 'none', padding: '0.5rem', width: '40px' }}></th>
                      </tr>
                    </thead>
                    <tbody style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                      {displayedJobs.length === 0 && (
                        <tr>
                          <td colSpan={9} style={{ border: 'none', padding: '1.5rem', textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>
                            No campaigns match “{searchQuery}”.
                          </td>
                        </tr>
                      )}
                      {displayedJobs.map((job) => (
                        <tr
                          key={job.id}
                          style={{
                            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                            cursor: 'pointer',
                            transition: 'background-color 0.15s',
                          }}
                          onClick={() => history.push(`/jobs/${job.id}`)}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                          <td style={{ border: 'none', padding: '0.5rem', whiteSpace: 'nowrap', minWidth: '230px' }}>
                            <span style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', flexWrap: 'nowrap' }}>
                              {getStatusBadge(job.status, job)}
                              {/* Downloaded fraction badge — always
                                  shown next to Delivered when the
                                  campaign has any delivery records.
                                  Green when everyone downloaded, amber
                                  for partial, muted when zero
                                  broadcasters have grabbed the delivery
                                  yet. Reflects that a single campaign
                                  may have many station-level deliveries
                                  with independent download status. */}
                              {(() => {
                                const total = (job as any).deliveryCount;
                                if (typeof total !== 'number' || total === 0) return null;
                                const downloaded = Number((job as any).downloadedDeliveryCount) || 0;
                                const isComplete = downloaded === total;
                                const isPartial = downloaded > 0 && !isComplete;
                                const bg = isComplete
                                  ? 'rgba(80, 200, 120, 0.3)'
                                  : isPartial
                                    ? 'rgba(255, 180, 50, 0.3)'
                                    : 'rgba(255, 255, 255, 0.1)';
                                const fg = isComplete
                                  ? '#50c878'
                                  : isPartial
                                    ? '#ffb432'
                                    : 'rgba(255,255,255,0.5)';
                                return (
                                  <Badge
                                    title={`${downloaded} of ${total} recipient${total === 1 ? '' : 's'} have downloaded`}
                                    style={{ backgroundColor: bg, color: fg }}
                                  >
                                    {downloaded}/{total} downloaded
                                  </Badge>
                                );
                              })()}
                            </span>
                          </td>
                          <td style={{ border: 'none', padding: '0.5rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                            {getClientName(job.client)}
                          </td>
                          <td style={{ border: 'none', padding: '0.5rem', color: 'var(--accent-1-color)', fontWeight: '500' }}>
                            {getCampaignName(job.campaign)}
                          </td>
                          <td style={{ border: 'none', padding: '0.5rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                            {job.period || '--'}
                          </td>
                          <td style={{ border: 'none', padding: '0.5rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                            {job.stationCount || 0}
                          </td>
                          <td style={{ border: 'none', padding: '0.5rem' }}>
                            {(() => {
                              const linked = getLinkedCount(job);
                              const liveReads = getLiveReadCount(job);
                              const linkable = getLinkableStationCount(job);
                              // Ready when every linkable spot is linked. An
                              // all-live-read campaign (linkable === 0) with
                              // stations > 0 is also "ready" — nothing left
                              // to link. Empty jobs stay amber.
                              const ready = (job.stationCount || 0) > 0 && linked === linkable;
                              return (
                                <span style={{ color: ready ? '#50c878' : '#ffb432' }}>
                                  {linked}/{linkable}
                                  {liveReads > 0 && (
                                    <span
                                      title={`${liveReads} Live Read spot${liveReads === 1 ? '' : 's'} — no audio to link`}
                                      style={{
                                        marginLeft: '6px',
                                        padding: '1px 5px',
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        borderRadius: '3px',
                                        backgroundColor: 'rgba(140, 100, 220, 0.18)',
                                        color: '#b394ff',
                                        letterSpacing: '0.3px',
                                      }}
                                    >+{liveReads} LR</span>
                                  )}
                                </span>
                              );
                            })()}
                          </td>
                          <td style={{ border: 'none', padding: '0.5rem', color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.85rem' }}>
                            {job.uploadedAt ? moment(job.uploadedAt).format('DD/MM/YYYY') : '--'}
                          </td>
                          <td style={{ border: 'none', padding: '0.5rem', color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.8rem' }}>
                            {(() => {
                              const isCrossOrgCopy = (job as any).isDeliveredJob || (job as any).isCampaignCopy;
                              const label = (isCrossOrgCopy ? (job as any).sourceOrgName : job.fileName) || '--';
                              // Chase-list filenames run long, so cap the column
                              // width and ellipsis-truncate — the full name stays
                              // available on hover via the title tooltip.
                              return (
                                <span
                                  title={label}
                                  style={{
                                    display: 'block',
                                    maxWidth: '150px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {isCrossOrgCopy && (
                                    <i className="tim-icons icon-cloud-download-93 mr-1" style={{ fontSize: '0.7rem' }}></i>
                                  )}
                                  {label}
                                </span>
                              );
                            })()}
                          </td>
                          <td style={{ border: 'none', padding: '0.5rem' }}>
                            <button
                              onClick={(e) => handleDeleteJob(e, job)}
                              disabled={deletingId === job.id}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'rgba(255, 255, 255, 0.3)',
                                cursor: deletingId === job.id ? 'not-allowed' : 'pointer',
                                fontSize: '1rem',
                                padding: '2px 6px',
                                transition: 'color 0.15s',
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.color = '#ff5050')}
                              onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.3)')}
                              title="Delete campaign"
                            >
                              {deletingId === job.id ? <Spinner size="sm" /> : <i className="tim-icons icon-trash-simple"></i>}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Chase List Uploader Modal */}
      <ChaseListUploader
        isOpen={showUploader}
        onClose={() => setShowUploader(false)}
        firebase={firebase}
        onSaved={(jobId) => {
          setShowUploader(false);
          // Navigate to the new job detail page
          history.push(`/jobs/${jobId}`);
        }}
      />

      {/* Styled delete-campaign confirmation.
          Three variants:
            - Copy (agency/studio viewing their local copy): reassuring — the
              source is unaffected.
            - Source, never delivered: standard cascade warning.
            - Source, ALREADY DELIVERED: stern warning — broadcasters have
              received this and will lose it. Uses red emphasis and lists the
              recipient count so the operator sees the blast radius. */}
      {deleteTarget && (() => {
        const isCopy = !!(deleteTarget as any).isCampaignCopy || !!(deleteTarget as any).isDeliveredJob;
        const isDelivered = !isCopy && (
          (deleteTarget as any).status === 'delivered' ||
          (Number((deleteTarget as any).deliveryCount) || 0) > 0
        );
        const deliveryCount = Number((deleteTarget as any).deliveryCount) || 0;
        return (
          <GenericConfirmModal
            isOpen={true}
            title={isDelivered ? 'Delete DELIVERED Campaign' : 'Delete Campaign'}
            body={
              <>
                Are you sure you want to delete
                {' '}<strong style={{ color: 'var(--accent-1-color)' }}>{getCampaignName(deleteTarget.campaign, 'this campaign')}</strong>?
                <br />
                {isCopy ? (
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
                    This removes only your copy — the source and other orgs are not affected.
                  </span>
                ) : isDelivered ? (
                  <>
                    <br />
                    <span style={{ color: '#ff5050', fontSize: '0.95rem', fontWeight: 600 }}>
                      ⚠ This campaign has already been delivered
                      {deliveryCount > 0 ? ` to ${deliveryCount} recipient${deliveryCount === 1 ? '' : 's'}` : ''}.
                    </span>
                    <br />
                    <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem' }}>
                      Deleting will:
                    </span>
                    <ul style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.85rem', paddingLeft: '1.25rem', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                      <li>Remove the delivered campaign and all its audio from every broadcaster's library</li>
                      <li>Remove the campaign from tagged studio / agency orgs</li>
                      <li>Disable every external delivery link for this campaign</li>
                      <li>Clean up pending broadcaster notifications</li>
                    </ul>
                    <span style={{ color: '#ff5050', fontSize: '0.85rem', fontWeight: 500 }}>
                      This cannot be undone.
                    </span>
                  </>
                ) : (
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
                    This will also remove the campaign and its linked audio spots from every tagged studio and agency. This cannot be undone.
                  </span>
                )}
              </>
            }
            callback={confirmDeleteJob}
            cancelCallback={() => setDeleteTarget(null)}
            onRequestClose={() => setDeleteTarget(null)}
          />
        );
      })()}
    </div>
  );
};

const condition = (authUser: any) => !!authUser;
export default withOrganization(withAuthorization(condition)(Jobs));
